import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import * as Mui from "@mui/material";
import type { WithStyles } from "type/tss";
import Autocomplete from "@mui/material/Autocomplete";
import * as Icons from "react-feather";
import { useSelector } from "react-redux";
import { useAppThunkDispatch, RootState } from "redux/store";
import { Agent } from "model/agent";
import { AgentBoard } from "model/agent-board";
import { updateAgent } from "redux/slice/agents";
import { createAgentBoard, deleteAgentBoard, updateAgentBoard } from "redux/slice/agent-boards";
import { getAgentBoardsForAgent, getBoardsForUser, getLanguages, getOffices, getUser } from "redux/selector";
import { LabeledField, LabeledControl } from "./labeled-field";
import { InfoBanner } from "component/shared/info-banner";
import * as paths from "routes/paths";
import * as Router from "react-router-dom";
import { styles } from "./styles";
import { TabHandle } from "./tab-handle";
import { MlsIds } from "./mls-ids";
import { mlsSetupRefresh } from "shared/env";

interface Props extends WithStyles<typeof styles> {
	agent: Agent;
	onDirtyChange: () => void;
	/** The logged-in member viewing their own Profile page (MAX-4139). */
	selfView?: boolean;
	/** Account has no team context — drops Languages and the MLS IDs section. */
	isSingleAgent?: boolean;
}

const MLS_HELPER =
	"Your agent's MLS ID connects their listings to their profile — required to show listings on their bio page, enable agent-specific listing shortcodes, and display their info on featured listings.";
// Same guidance addressed to the member reading their own profile (MAX-4139).
const MLS_HELPER_SELF =
	"Your MLS ID connects your listings to your profile — required to show listings on your bio page, enable agent-specific listing shortcodes, and display your info on featured listings.";

export const ProfileTab = forwardRef<TabHandle, Props>(({ agent, selfView = false, isSingleAgent = false, classes = {}, onDirtyChange }, ref) => {
	const dispatch = useAppThunkDispatch();
	const user = useSelector(getUser);
	// The MLS Setup redesign's MLS IDs, where the account can read them; otherwise the agent-board editor.
	const usesAgentMlsIds = mlsSetupRefresh && user.permissions.officeAccountType === true;
	// Tier gating applies to self-view only — the member-detail view ships in production via
	// refreshTeam and must keep showing these for every agent an admin manages. Single-agent
	// accounts drop them regardless of tier.
	const showTeamFields = !isSingleAgent
		&& (!selfView || !!(user.permissions.broker && user.permissions.crm && user.permissions.showTeamFeatures));
	const offices = useSelector(getOffices);
	const languages = useSelector(getLanguages);
	const boards = useSelector(getBoardsForUser);
	const initialBoards = useSelector((state: RootState) => getAgentBoardsForAgent(state, agent.id));

	const [firstName, setFirstName] = useState(agent.firstName || "");
	const [lastName, setLastName] = useState(agent.lastName || "");
	const [designation, setDesignation] = useState(agent.designation || "");
	const [officeId, setOfficeId] = useState(agent.office?.id || 0);
	const [languageIds, setLanguageIds] = useState<number[]>(agent.languageIds || []);
	const [agentBoards, setAgentBoards] = useState<AgentBoard[]>(initialBoards);
	const [email, setEmail] = useState(agent.email || "");
	const [cellPhone, setCellPhone] = useState(agent.cellPhone || "");
	const [officePhone, setOfficePhone] = useState(agent.officePhone || "");
	const [homePhone, setHomePhone] = useState(agent.homePhone || "");
	const [address, setAddress] = useState(agent.officeAddress || "");
	const [city, setCity] = useState(agent.officeCity || "");
	const [state, setStateVal] = useState(agent.officeState || "");
	const [zip, setZip] = useState(agent.officeZip || "");
	const [errors, setErrors] = useState<Partial<Record<"firstName" | "lastName" | "email", string>>>({});

	const boardsKey = (rows: AgentBoard[]) => rows.map((b) => `${b.id}:${b.boardId}:${b.agentCode}:${b.agentId}`).join("|");

	// MAX-4315: fetchAgentBoards() is fire-and-forget on app boot, so this tab can mount before
	// the boards arrive and bake an empty list into state. Adopt late rows, but only while the
	// user hasn't edited — an unconditional resync would re-introduce MAX-4311.
	const hydratedAgentIdRef = useRef<number | null>(null);
	const boardsTouchedRef = useRef(false);
	useEffect(() => {
		if (boardsTouchedRef.current) {
			return;
		}
		const switchedAgent = hydratedAgentIdRef.current !== agent.id;
		if (switchedAgent) {
			hydratedAgentIdRef.current = agent.id;
			setAgentBoards(initialBoards);
			return;
		}
		// Same agent: only fill in a list we never had, so an echo can't overwrite real edits.
		setAgentBoards((prev) => (prev.length === 0 && initialBoards.length > 0 ? initialBoards : prev));
	}, [agent.id, initialBoards]);
	const isDirty = () =>
		firstName !== (agent.firstName || "") ||
		lastName !== (agent.lastName || "") ||
		designation !== (agent.designation || "") ||
		officeId !== (agent.office?.id || 0) ||
		languageIds.join(",") !== (agent.languageIds || []).join(",") ||
		// With the MLS Setup redesign on, MLS IDs save through their own modal, not the save bar.
		(!usesAgentMlsIds && boardsKey(agentBoards) !== boardsKey(initialBoards)) ||
		email !== (agent.email || "") ||
		cellPhone !== (agent.cellPhone || "") ||
		officePhone !== (agent.officePhone || "") ||
		homePhone !== (agent.homePhone || "") ||
		address !== (agent.officeAddress || "") ||
		city !== (agent.officeCity || "") ||
		state !== (agent.officeState || "") ||
		zip !== (agent.officeZip || "");

	// Notify the shell (owns the shared save bar) only when a field actually changes.
	useEffect(() => { onDirtyChange(); }, [
		onDirtyChange, firstName, lastName, designation, officeId, languageIds, agentBoards,
		email, cellPhone, officePhone, homePhone, address, city, state, zip,
	]);

	const REQUIRED = "This field is required";
	const validate = () => {
		const next: typeof errors = {};
		if (!firstName.trim()) next.firstName = REQUIRED;
		if (!lastName.trim()) next.lastName = REQUIRED;
		if (!email.trim()) next.email = REQUIRED;
		setErrors(next);
		return Object.keys(next).length === 0;
	};
	const editRequired = (field: keyof typeof errors, setter: (value: string) => void) => (value: string) => {
		setter(value);
		setErrors((prev) => ({ ...prev, [field]: undefined }));
	};

	// Row edits park the hydrate effect above so echoes can't overwrite them.
	const addMlsRow = () => {
		boardsTouchedRef.current = true;
		setAgentBoards([...agentBoards, { id: 0, agentCode: "", agentId: agent.id, boardId: boards[0]?.id || 0 }]);
	};
	const updateMlsRow = (index: number, patch: Partial<AgentBoard>) => {
		boardsTouchedRef.current = true;
		setAgentBoards(agentBoards.map((ab, i) => (i === index ? { ...ab, ...patch } : ab)));
	};
	// Mark existing rows for deletion by clearing agentId (matches the AgentSettingsTab save contract);
	// brand-new rows (id===0) are simply dropped from the array.
	const removeMlsRow = (index: number) => {
		boardsTouchedRef.current = true;
		setAgentBoards(
			agentBoards
				.map((ab, i) => (i === index ? { ...ab, agentId: 0 } : ab))
				.filter((ab) => !(ab.id === 0 && ab.agentId === 0))
		);
	};

	useImperativeHandle(ref, () => ({
		isDirty,
		validate,
		cancel: () => {
			setErrors({});
			setFirstName(agent.firstName || "");
			setLastName(agent.lastName || "");
			setDesignation(agent.designation || "");
			setOfficeId(agent.office?.id || 0);
			setLanguageIds(agent.languageIds || []);
			boardsTouchedRef.current = false;
			setAgentBoards(initialBoards);
			setEmail(agent.email || "");
			setCellPhone(agent.cellPhone || "");
			setOfficePhone(agent.officePhone || "");
			setHomePhone(agent.homePhone || "");
			setAddress(agent.officeAddress || "");
			setCity(agent.officeCity || "");
			setStateVal(agent.officeState || "");
			setZip(agent.officeZip || "");
		},
		save: async () => {
			// .unwrap() so an API failure rejects here instead of resolving into a false success.
			const resource = await dispatch(updateAgent({
				agent: {
					...agent,
					firstName, lastName, designation,
					office: offices.find((o) => o.id === officeId),
					languageIds,
					email, cellPhone, officePhone, homePhone,
					officeAddress: address, officeCity: city, officeState: state, officeZip: zip,
				},
			})).unwrap();
			if (!usesAgentMlsIds && resource && resource.id) {
				const agentId = resource.id;
				// MAX-4316/4317: save() used to leave agentBoards untouched, so a created row kept
				// its sentinel id 0 and the next Save re-created it, and a removed row stayed on
				// screen. Reconcile from each thunk's resolved payload rather than the store, which
				// lags behind an un-awaited dispatch.
				const saved: AgentBoard[] = [];
				for (const ab of agentBoards.filter((b) => b.id && b.agentId && b.agentCode)) {
					const res = await dispatch(updateAgentBoard({ agentBoard: ab, agentId })).unwrap();
					saved.push((res as AgentBoard) || ab);
				}
				for (const ab of agentBoards.filter((b) => b.id && !b.agentId)) {
					await dispatch(deleteAgentBoard({ agentBoard: ab })).unwrap();
					// Not pushed — the row is gone and must stop rendering.
				}
				for (const ab of agentBoards.filter((b) => !b.id && b.agentCode)) {
					// Keep the server id so a second Save updates instead of re-creating.
					const created = await dispatch(createAgentBoard({ agentBoard: ab, agentId })).unwrap() as AgentBoard | undefined;
					if (created && created.id) {
						saved.push(created);
					}
				}
				// Also drops rows that were never persistable (blank agentCode).
				boardsTouchedRef.current = false;
				setAgentBoards(saved);
			}
		},
	}));

	// Rows marked for deletion (agentId 0) are hidden alongside unsaved blanks, so Remove takes
	// effect on screen right away and stays gone until Save or Cancel (MAX-4317).
	const visibleMlsRows = agentBoards.filter((ab) => ab.agentId !== 0);

	// Only the account owner looking at their own profile, and only if they can reach the Account
	// page the banner points at. It resolves an ambiguity specific to editing your own details --
	// an admin managing someone else does not have it, so the member-detail view never shows this.
	const showAccountOwnerBanner = selfView && !!agent.isAccountOwner && !!user.admin;

	return (
		<div>
			{showAccountOwnerBanner && (
				<InfoBanner
					action={
						<Mui.Link component={Router.Link} to={paths.settingsAccount}>
							Go to Account →
						</Mui.Link>
					}
				>
					You're viewing your personal profile, which controls how you're displayed on the site.
					Account information (billing, MLS compliance, contact details) is managed separately.
				</InfoBanner>
			)}

			{/* General */}
			<div className={classes.section}>
				<Mui.Typography className={classes.sectionTitle}>General</Mui.Typography>
				<Mui.Grid container spacing={2} className={classes.formColumn}>
					<Mui.Grid item xs={12} sm={6}>
						<LabeledField label="First Name" required value={firstName} onChange={editRequired("firstName", setFirstName)} error={!!errors.firstName} helperText={errors.firstName} />
					</Mui.Grid>
					<Mui.Grid item xs={12} sm={6}>
						<LabeledField label="Last Name" required value={lastName} onChange={editRequired("lastName", setLastName)} error={!!errors.lastName} helperText={errors.lastName} />
					</Mui.Grid>
					<Mui.Grid item xs={12}>
						<LabeledField label="Title / Designation" value={designation} onChange={setDesignation} />
					</Mui.Grid>
					<Mui.Grid item xs={12}>
						<LabeledField
							label="Office"
							select
							value={String(officeId)}
							onChange={(v) => setOfficeId(Number(v))}
						>
							<Mui.MenuItem value={0}>–</Mui.MenuItem>
							{offices.map((o) => <Mui.MenuItem key={o.id} value={o.id}>{o.name}</Mui.MenuItem>)}
						</LabeledField>
					</Mui.Grid>
					{showTeamFields && <Mui.Grid item xs={12}>
						<LabeledControl label="Languages">
							<Autocomplete
								multiple
								disableCloseOnSelect
								options={languages}
								getOptionLabel={(option) => option.name}
								value={languages.filter((l) => languageIds.includes(l.id))}
								onChange={(_e, selected) => setLanguageIds(selected.map((l) => l.id))}
								size="small"
								className={classes.languagesSelect}
								renderTags={(selected, getTagProps) =>
									selected.map((lang, index) => (
										<Mui.Chip
											{...getTagProps({ index })}
											key={lang.id}
											label={lang.name}
											deleteIcon={<Icons.X size={16} />}
											className={classes.languageChip}
										/>
									))
								}
								renderInput={(params) => <Mui.TextField {...params} variant="outlined" />}
							/>
						</LabeledControl>
					</Mui.Grid>}
				</Mui.Grid>
			</div>

			{/* MLS IDs */}
			{showTeamFields && <div className={classes.section}>
				<Mui.Typography className={classes.sectionTitle}>MLS IDs</Mui.Typography>
				{usesAgentMlsIds && <MlsIds agent={agent} />}
				{!usesAgentMlsIds && <Mui.Typography className={classes.sectionDescription}>{selfView ? MLS_HELPER_SELF : MLS_HELPER}</Mui.Typography>}
				{!usesAgentMlsIds && <div className={classes.formColumn}>
				{visibleMlsRows.map((ab) => {
					const realIndex = agentBoards.indexOf(ab);
					return (
						<Mui.Grid container spacing={2} alignItems="flex-start" key={realIndex} className={classes.mlsRow}>
							<Mui.Grid item xs={12} sm={5}>
								<LabeledField
									label="MLS"
									select
									value={String(ab.boardId)}
									onChange={(v) => updateMlsRow(realIndex, { boardId: Number(v) })}
								>
									{boards.map((b) => <Mui.MenuItem key={b.id} value={b.id}>{b.shortName}</Mui.MenuItem>)}
								</LabeledField>
							</Mui.Grid>
							<Mui.Grid item xs={12} sm={5}>
								<LabeledField label="Agent ID" value={ab.agentCode} onChange={(v) => updateMlsRow(realIndex, { agentCode: v })} />
							</Mui.Grid>
							<Mui.Grid item xs={12} sm={2} className={classes.mlsRemove}>
								<Mui.Button className={classes.mlsRemoveButton} onClick={() => removeMlsRow(realIndex)}>Remove</Mui.Button>
							</Mui.Grid>
						</Mui.Grid>
					);
				})}
				<Mui.Button color="primary" startIcon={<Icons.Plus size={16} />} onClick={addMlsRow}>Add MLS ID</Mui.Button>
				</div>}
			</div>}

			{/* Contact Information */}
			<div className={classes.section}>
				<Mui.Typography className={classes.sectionTitle}>Contact Information</Mui.Typography>
				<Mui.Grid container spacing={2} className={classes.formColumn}>
					<Mui.Grid item xs={12} sm={6}><LabeledField label="Email" required value={email} onChange={editRequired("email", setEmail)} error={!!errors.email} helperText={errors.email} /></Mui.Grid>
					<Mui.Grid item xs={12} sm={6}><LabeledField label="Cell Phone" value={cellPhone} onChange={setCellPhone} /></Mui.Grid>
					<Mui.Grid item xs={12} sm={6}><LabeledField label="Office Phone" value={officePhone} onChange={setOfficePhone} /></Mui.Grid>
					<Mui.Grid item xs={12} sm={6}><LabeledField label="Home Phone" value={homePhone} onChange={setHomePhone} /></Mui.Grid>
					<Mui.Grid item xs={12}><LabeledField label="Address" value={address} onChange={setAddress} /></Mui.Grid>
					<Mui.Grid item xs={12} sm={6}><LabeledField label="City" value={city} onChange={setCity} /></Mui.Grid>
					<Mui.Grid item xs={12} sm={6}><LabeledField label="State" value={state} onChange={setStateVal} /></Mui.Grid>
					<Mui.Grid item xs={12}><LabeledField label="ZIP Code" value={zip} onChange={setZip} /></Mui.Grid>
				</Mui.Grid>
			</div>

		</div>
	);
});
